import { FC, useState } from "react";
import { Button, Checkbox, Modal, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { StyledCheckboxContainer, StyledModalActions } from "./StyledComponents";
import { FormLabel } from "@kaltura/mediaspace-shared-ui/form";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface WelcomeProps {
    /**
     * Whether the modal is open
     */
    open: boolean;

    /**
     * Callback when the modal is closed
     */
    onClose: () => void;

    /**
     * Callback when the "Don't show again" checkbox is changed
     */
    onDontShowAgainChange?: (dontShowAgain: boolean) => void;
}

/**
 * Welcome modal for the Live Studio
 */
export const Welcome: FC<WelcomeProps> = ({ open, onClose, onDontShowAgainChange }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setDontShowAgain(checked);
        sendButtonAnalytics((checked ? "Don’t " : "") + "show the Studio view modal", ButtonClickAnalyticsType.TOGGLE);
        if (onDontShowAgainChange) {
            onDontShowAgainChange(checked);
        }
    };

    const handleClose = () => {
        sendButtonAnalytics("Close Viewing as Studio modal", ButtonClickAnalyticsType.CLOSE);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="welcome-modal-title"
            className="kms-ds-live-studio-welcome-modal"
        >
            <ModalTitle id="welcome-modal-title">{translate("Welcome to the Studio")}</ModalTitle>
            <ModalContent>
                <Typography variant="body1" align="center" className="kms-ds-welcome-modal-description">
                    {translate(
                        "Prepare and manage your broadcast from here. To see the attendee experience, click the Attendee view button."
                    )}
                </Typography>
            </ModalContent>

            <StyledModalActions className="kms-ds-welcome-modal-actions">
                <StyledCheckboxContainer>
                    <Checkbox
                        id="dont-show-again"
                        checked={dontShowAgain}
                        onChange={handleCheckboxChange}
                        className="kms-ds-welcome-modal-checkbox"
                    />
                    <FormLabel htmlFor="dont-show-again" text={translate("Don't show again")} />
                </StyledCheckboxContainer>

                <Button onClick={handleClose} className="kms-ds-welcome-modal-close-button">
                    {translate("Close")}
                </Button>
            </StyledModalActions>
        </Modal>
    );
};

export default Welcome;
